import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
});

export interface ClassInfo {
  id: string;
  name: string;
}

export interface Student {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  notes?: string;
  active: boolean;
  created_at: string;
  classes: ClassInfo[];
}

export interface ClassOut {
  id: string;
  name: string;
  description?: string;
  schedule?: string;
  active: boolean;
  telegram_chat_id?: string;
  telegram_hw_topic_id?: string;
  telegram_reminder_topic_id?: string;
  created_at: string;
  student_count: number;
  students?: { id: string; name: string }[];
}

export interface AttendanceEntry {
  student_id: string;
  status: string;
  notes?: string;
}

export interface SessionOut {
  id: string;
  class_id: string;
  date: string;
  lesson_number?: number;
  topic?: string;
  notes?: string;
  duration_min?: number;
  created_at: string;
  attendance_count?: number;
  present_count?: number;
  attendances?: { id: string; student_id: string; status: string; notes?: string }[];
}

export interface GridColumn {
  id: string;
  date: string;
  day: string;
  day_num: number;
  month: string;
  lesson_number?: number;
  topic?: string;
}

export interface AttendanceGrid {
  class_id: string;
  class_name: string;
  columns: GridColumn[];
  students: { id: string; name: string }[];
  grid: Record<string, Record<string, string>>; // student_id -> session_id -> status
}

export interface Assignment {
  id: string;
  class_id: string;
  title: string;
  description?: string;
  due_date?: string;
  created_at: string;
}

export interface Submission {
  id: string;
  assignment_id: string;
  student_id: string;
  file_urls?: string;
  text_content?: string;
  submitted_at: string;
  source: string;
}

export interface FollowUp {
  id: string;
  student_id: string;
  reason: string;
  type: string;
  status: string;
  notes?: string;
  created_at: string;
  resolved_at?: string;
}

export interface DashboardStats {
  total_students: number;
  total_classes: number;
  total_assignments: number;
  pending_follow_ups: number;
  attendance_rate_30d: number;
}

export interface StudentSummary {
  total_sessions: number;
  attended: number;
  attendance_rate: number;
  total_assignments: number;
  submitted: number;
  completion_rate: number;
}

// Classes
export const getClasses = (activeOnly = true) =>
  api.get<ClassOut[]>("/classes", { params: { active_only: activeOnly } });
export const getClass = (id: string) => api.get<ClassOut>(`/classes/${id}`);
export const createClass = (data: { name: string; description?: string; schedule?: string }) =>
  api.post<ClassOut>("/classes", data);
export const updateClass = (id: string, data: any) =>
  api.patch<ClassOut>(`/classes/${id}`, data);
export const deleteClass = (id: string) => api.delete(`/classes/${id}`);
export const addStudentToClass = (classId: string, studentId: string) =>
  api.post(`/classes/${classId}/students/${studentId}`);
export const removeStudentFromClass = (classId: string, studentId: string) =>
  api.delete(`/classes/${classId}/students/${studentId}`);

// Students
export const getStudents = (activeOnly = true) =>
  api.get<Student[]>("/students", { params: { active_only: activeOnly } });
export const getStudent = (id: string) => api.get<Student>(`/students/${id}`);
export const createStudent = (data: { name: string; phone?: string; email?: string; notes?: string }) =>
  api.post<Student>("/students", data);
export const updateStudent = (id: string, data: any) =>
  api.patch<Student>(`/students/${id}`, data);
export const deleteStudent = (id: string) => api.delete(`/students/${id}`);

// Sessions
export const getSessions = (classId?: string) =>
  api.get<SessionOut[]>("/sessions", { params: { class_id: classId } });
export const getSession = (id: string) => api.get<SessionOut>(`/sessions/${id}`);
export const getAttendanceGrid = (classId: string) =>
  api.get<AttendanceGrid>(`/sessions/grid/${classId}`);
export const createSession = (data: {
  class_id: string;
  date: string;
  lesson_number?: number;
  topic?: string;
  notes?: string;
  duration_min?: number;
  attendances: AttendanceEntry[];
}) => api.post("/sessions", data);
export const updateCell = (data: { session_id: string; student_id: string; status: string }) =>
  api.put("/sessions/cell", data);
export const updateSession = (id: string, data: { date?: string; topic?: string; lesson_number?: number }) =>
  api.patch(`/sessions/${id}`, data);
export const generateLessons = (data: { class_id: string; start_date: string; end_date: string; start_lesson_number?: number }) =>
  api.post("/sessions/generate", data);
export const updateAttendance = (sessionId: string, entries: AttendanceEntry[]) =>
  api.put(`/sessions/${sessionId}/attendance`, entries);
export const deleteSession = (id: string) => api.delete(`/sessions/${id}`);

// Assignments
export const getAssignments = (classId?: string) =>
  api.get<Assignment[]>("/assignments", { params: { class_id: classId } });
export const createAssignment = (data: {
  class_id: string;
  title: string;
  description?: string;
  due_date?: string;
  questions?: { question_text: string; answer_text?: string }[];
}) => api.post<Assignment>("/assignments", data);
export const deleteAssignment = (id: string) => api.delete(`/assignments/${id}`);

// Submissions
export const getSubmissions = (assignmentId?: string, studentId?: string) =>
  api.get<Submission[]>("/submissions", {
    params: { assignment_id: assignmentId, student_id: studentId },
  });
export const createSubmission = (formData: FormData) =>
  api.post<Submission>("/submissions", formData);
export const deleteSubmission = (id: string) => api.delete(`/submissions/${id}`);

// Follow-ups
export const getFollowUps = (studentId?: string, status?: string) =>
  api.get<FollowUp[]>("/follow-ups", { params: { student_id: studentId, status } });
export const createFollowUp = (data: any) => api.post<FollowUp>("/follow-ups", data);
export const updateFollowUp = (id: string, data: any) => api.patch<FollowUp>(`/follow-ups/${id}`, data);

// Dashboard
export const getDashboardStats = () => api.get<DashboardStats>("/dashboard/stats");
export const getStudentSummary = (studentId: string) =>
  api.get<StudentSummary>(`/dashboard/student/${studentId}/summary`);

// Curriculum
export interface CurriculumItem {
  id: string;
  lesson_number: number;
  title: string;
}
export const getCurriculum = (classId: string) =>
  api.get<CurriculumItem[]>(`/curriculum/${classId}`);
export const addCurriculumItem = (classId: string, data: { lesson_number: number; title: string }) =>
  api.post(`/curriculum/${classId}`, data);
export const replaceCurriculum = (classId: string, items: { lesson_number: number; title: string }[]) =>
  api.put(`/curriculum/${classId}`, { items });
export const deleteCurriculumItem = (classId: string, itemId: string) =>
  api.delete(`/curriculum/${classId}/${itemId}`);

// Homework (public)
export const getHomework = (classId: string) => api.get<any>(`/homework/${classId}`);
export const scanTopics = (classId: string) =>
  api.post(`/homework/${classId}/scan-topics`);
export const getStudentAnswers = (classId: string, studentId: string) =>
  api.get<Record<string, { answer_text: string; submitted_at: string }>>(`/homework/${classId}/student/${studentId}`);
export const submitHomework = (classId: string, data: { student_id: string; answers: { question_id: string; answer_text: string }[] }) =>
  api.post(`/homework/${classId}/submit`, data);

export default api;
